import { Link, Stack, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

import { layout, space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { API_URL } from "../src/env";
import { log } from "../src/log";
import { type Colors, useColors } from "../src/theme";

// Landing page for the verify-email link. The token is generated by
// BetterAuth on signup (or via the resend-from-settings mutation) and
// embedded in the email URL pointing here. We call BetterAuth's
// /auth/verify-email?token=… on mount, then render success or failure.
type Status = "verifying" | "verified" | "failed";

export default function VerifyEmail(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const params = useLocalSearchParams<{ token?: string }>();
  const token = typeof params.token === "string" ? params.token : null;

  const [status, setStatus] = useState<Status>(token ? "verifying" : "failed");
  const [error, setError] = useState<string | null>(
    token ? null : "Missing verification token.",
  );
  // BetterAuth's /auth/verify-email is a one-shot — calling it twice
  // with a consumed token returns INVALID_TOKEN. React StrictMode (and
  // dev fast refresh) double-fire effects, so guard against the second
  // call rather than letting the user see a confusing failure state.
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/auth/verify-email?token=${encodeURIComponent(token)}`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!res.ok) {
          const body = (await res.json().catch((parseErr) => {
            log.warn({ status: res.status, err: String(parseErr) }, "verify-email response not JSON");
            return {};
          })) as { message?: string };
          setError(body.message ?? `verification failed (${res.status})`);
          setStatus("failed");
          return;
        }
        setStatus("verified");
      } catch (err) {
        setError(err instanceof Error ? err.message : "verification failed");
        setStatus("failed");
      }
    })();
  }, [token]);

  return (
    <>
      <Stack.Screen options={{ title: "Verify email" }} />
      <View style={styles.container}>
        {status === "verifying" ? (
          <>
            <ActivityIndicator color={colors.accentSoftText} />
            <Text style={styles.subtitle}>Confirming your email…</Text>
          </>
        ) : status === "verified" ? (
          <>
            <Text style={styles.title}>Email confirmed</Text>
            <Text style={styles.subtitle}>
              You&apos;re all set. Continue to start studying.
            </Text>
            <Button
              label="Continue"
              onPress={() => router.replace("/login")}
            />
          </>
        ) : (
          <>
            <Text style={styles.title}>Couldn&apos;t verify</Text>
            <Text style={styles.subtitle}>
              {error ?? "The link is invalid or expired."}
            </Text>
            {/* This screen only has the (now-consumed/expired) token, not
                the email address, so it can't resend directly. Send the
                user to login, where an unverified sign-in surfaces a
                "Resend verification email" action. */}
            <Text style={styles.subtitle}>
              Try logging in to request a fresh link.
            </Text>
            <Link href="/login" asChild>
              <Button label="Back to login" />
            </Link>
          </>
        )}
      </View>
    </>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
    gap: space[4],
    maxWidth: layout.formMaxWidth,
    alignSelf: "center",
    width: "100%",
  },
  title: { ...textStyle.h2, color: colors.heading, textAlign: "center" },
  subtitle: {
    ...textStyle.bodySmall,
    color: colors.textSecondary,
    textAlign: "center",
  },
  });
}
